/**
 * @fileoverview Console transport with styling for browser
 * @module transports/console
 */

import { LOG_LEVEL } from '../constants.js';

/**
 * Console transport for browser logging
 */
export class ConsoleTransport {
  /**
   * Create console transport
   * @param {Object} [config] - Transport configuration
   * @param {boolean} [config.styled=true] - Enable styled output
   */
  constructor(config = {}) {
    this.config = {
      styled: true,
      ...config,
    };
  }

  /**
   * Log entry to console
   * @param {Object} entry - Log entry
   */
  log(entry) {
    const { level, message, timestamp, ...rest } = entry;
    const consoleMethod = this.getConsoleMethod(level);

    if (this.config.styled) {
      const style = this.getStyle(level);
      consoleMethod(`%c[${level}]%c ${message}`, style, 'color: inherit', rest);
    } else {
      consoleMethod(`[${timestamp}] [${level}] ${message}`, rest);
    }
  }

  /**
   * Get console method for log level
   * @param {string} level - Log level
   * @returns {Function} Console method
   *
   * `web/no-debug-code` bans `console.debug` because callers should route
   * through a logger instead. THIS IS that logger's console transport — the one
   * sanctioned sink where the console call actually has to happen. Suppressing
   * it here is not a waiver of the rule; it is the rule's own terminus.
   * (WW-ANCHOR-001 Lane 13.)
   */
  getConsoleMethod(level) {
    switch (level) {
      case LOG_LEVEL.ERROR:
      case LOG_LEVEL.CRITICAL:
        return console.error.bind(console);
      case LOG_LEVEL.WARN:
        return console.warn.bind(console);
      case LOG_LEVEL.INFO:
        return console.info.bind(console);
      case LOG_LEVEL.DEBUG:
        // eslint-disable-next-line web/no-debug-code -- this IS the canonical browser logger's console transport; the DEBUG level must map to console.debug here or the sink cannot honour its own level contract.
        return console.debug.bind(console);
      case LOG_LEVEL.TRACE:
      default:
        return console.log.bind(console);
    }
  }

  /**
   * Get CSS style for log level
   * @param {string} level - Log level
   * @returns {string} CSS style string
   */
  getStyle(level) {
    switch (level) {
      case LOG_LEVEL.CRITICAL:
        return 'color: white; background: #8B0000; font-weight: bold; padding: 2px 4px;';
      case LOG_LEVEL.ERROR:
        return 'color: #ff0000; font-weight: bold;';
      case LOG_LEVEL.WARN:
        return 'color: #ff8c00; font-weight: bold;';
      case LOG_LEVEL.INFO:
        return 'color: #0066cc;';
      case LOG_LEVEL.DEBUG:
        return 'color: #666666;';
      case LOG_LEVEL.TRACE:
      default:
        return 'color: #999999;';
    }
  }
}

/**
 * Factory function to create console transport
 * @param {Object} [config] - Transport configuration
 * @returns {ConsoleTransport} Console transport instance
 */
export function createConsoleTransport(config) {
  return new ConsoleTransport(config);
}
